import React, { useEffect, useMemo, useState } from 'react'
import { ScrollView, StyleSheet } from 'react-native'
import { compose } from 'redux'

import {
  DNABox,
  DNAButton,
  DNADivider,
  Iffy,
  Tabs,
  util,
} from '@alucio/lux-ui'

import { useSort, withSort } from 'src/components/DNA/hooks/useSort'
import { useAllDocumentsInstance } from 'src/state/redux/selector/document'
import { merge, filters } from 'src/state/redux/document/query'

import DocumentDropzone from './DocumentDropzone/DocumentDropzone';
import DNAPublisherList from 'src/components/DNA/GridList/DNAPublisherGridList'
import {
  DNADocumentFiltersChips,
  useDNADocumentFilters,
  withDNADocumentFilters,
} from 'src/components/DNA/Document/DNADocumentFilters'
import SelectFileButton, { useWebsiteDocumentCreation } from 'src/components/SelectFileButton/SelectFileButton';
import DNAPagination from 'src/components/DNA/Pagination/DNAPagination'
import useFixedHeader from '@alucio/lux-ui/src/hooks/useFixedLayout'
import { exportData } from './Export'
import { useCurrentUser } from 'src/state/redux/selector/user'
import { useTenant, useTenantList } from 'src/state/redux/selector/tenant'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import useCurrentPage from 'src/components/DNA/hooks/useCurrentPage'
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags'
import FiltersSidebar from 'src/components/QuickFilters/FiltersSidebar'
import FilterButton, { VariantOptions } from 'src/components/QuickFilters/FilterButton'
import QuickFiltersProvider from 'src/components/QuickFilters/QuickFiltersProvider'
import ActiveUser from 'src/state/global/ActiveUser'
import { TitleBar } from 'src/components/DNA'
import VersioningPanel from '../Publishers/Versioning/VersioningPanel/VersioningPanel'
import DocumentTitleSearchInput from 'src/components/DocumentTitleSearchInput';
import { useDocumentSearch } from 'src/state/redux/selector/documentSearch/documentSearch';

// Wrapper component that disables button for 5 seconds after clicking
const DisableButtonWrapper: React.FC<{
  onPress: () => void
  children: React.ReactNode
  disabled?: boolean
  [key: string]: any
}> = ({ onPress, children, disabled = false, ...props }) => {
  const [isDisabled, setIsDisabled] = useState(false)

  const handlePress = () => {
    if (!isDisabled && !disabled) {
      setIsDisabled(true)
      onPress()

      // Re-enable button after 5 seconds
      setTimeout(() => {
        setIsDisabled(false)
      }, 5000)
    }
  }

  return (
    <DNAButton
      {...props}
      disabled={isDisabled || disabled}
      onPress={handlePress}
    >
      {children}
    </DNAButton>
  )
}

const styles = StyleSheet.create({
  mainWrapper: {
    backgroundColor: colors['color-text-white'],
    flex: 1,
    flexDirection: 'column',
    minHeight: 'calc(100vh - 160px)',
    minWidth: 'auto',
  },
  filtersSidebarScrollView: {
    maxHeight: '100vh',
    minWidth: 420,
    maxWidth: 420,
    position: 'sticky' as any,
    top: 0,
    zIndex: 10,
    paddingBottom: 350,
  },
  filersSidebarNotVisible: {
    display: 'none',
  },
});

export const DNAPublisherLibrary: React.FC = () => {
  const { createWebsiteDocument, documentId, setDocumentId } = useWebsiteDocumentCreation()
  const [filesToUpload, setFilesToUpload] = useState<File[]>([])
  const featureFlags = useFeatureFlags(
    'enableWebVideoDocuments',
    'enablePublisherUpload',
  )
  const allDocumentsCSV = useAllDocumentsInstance()
  const tenants = useTenantList();
  const currentUser = useCurrentUser();

  const { sortSelectorOpts } = useSort()
  const fixedHeaderWidth = useFixedHeader();
  const {
    unpublishedToggle,
    toggleUnpublished,
    filterEntries,
    filterSelectorOpts,
    resetToDefaultFilters,
    clearFilters,
    setActiveFilters,
    applyDefaultFiltersToInProgress,
  } = useDNADocumentFilters()
  const { quickFilters } =  { ...useTenant(ActiveUser.user!.tenantId)?.tenant.config }
  const [filtersVisible, setFiltersVisible] = useState(false);

  const [filtersMinimumWidth, setFiltersMinimumWidth] = useState<number>();
  const [titleSearchText, setTitleSearchText] = useState('');
  const trimmedTitleSearchText = titleSearchText.trim();
  const titleSearchOptions = useMemo(
    () => ({ text: trimmedTitleSearchText, fields: ['title'], isPublisher: true }),
    [trimmedTitleSearchText],
  );
  const titleSearchResults = useDocumentSearch(titleSearchOptions);
  const titleSearchSelectorOpts = useMemo(() => {
    if (!trimmedTitleSearchText) return undefined;

    const documentIds = new Set(titleSearchResults.map(({ model }) => model.id));
    return { filter: { model: { id: (id: string) => documentIds.has(id) } } };
  }, [trimmedTitleSearchText, titleSearchResults]);

  const selectorOpts = useMemo(
    () => merge(
      filters.nonDeleted,
      filterSelectorOpts,
      sortSelectorOpts,
      titleSearchSelectorOpts,
    ),
    [sortSelectorOpts, filterSelectorOpts, titleSearchSelectorOpts],
  )

  const filteredDocuments = useAllDocumentsInstance(selectorOpts)
  const currentPage = useCurrentPage()

  function onSelectedFiles(files: File[]): void {
    setFilesToUpload(files);
  }
  const tabs = [
    {
      title: 'All',
      style: {
        width: 88,
      },
    },
    {
      title: 'In Progress',
    },
  ];

  const [selectedTab, setSelectedTab] = useState(tabs[unpublishedToggle ? 1 : 0].title);

  useEffect(() => {
    setSelectedTab(tabs[unpublishedToggle ? 1 : 0].title);

    if (
      unpublishedToggle &&
      !applyDefaultFiltersToInProgress
    ) {
      clearFilters()
    } else if (unpublishedToggle) {
      setActiveFilters(() => filterEntries.filter(entry => entry.default && entry.fieldId !== 'Status'))
    } else {
      resetToDefaultFilters()
    }
  }, [unpublishedToggle, applyDefaultFiltersToInProgress])

  return (
    <QuickFiltersProvider quickFilters={quickFilters ?? []}>
      <DNABox style={styles.mainWrapper} spacing="md">
        <DNABox fill spacing="md" appearance="col">
          {/* Header */}
          <DNABox style={{ maxWidth : fixedHeaderWidth }}>
            <TitleBar
              title={currentPage?.LABEL ?? ''}
              description="Manage and track all your content"
              showEllipsis
              ellipsisMenu={
                  {
                    items: [
                      {
                        label: 'Export content list',
                        icon: 'download-simple',
                        onPress: () => {
                          exportData(allDocumentsCSV, currentUser, tenants)
                        },
                      },
                    ],
                  }
                }
            >
              <TitleBar.Button>
                <Iffy is={featureFlags.enableWebVideoDocuments}>
                  <DisableButtonWrapper
                    onPress={createWebsiteDocument}
                    appearance="outline"
                    status="primary"
                    size="md"
                    children="Create website document"
                  />
                </Iffy>
              </TitleBar.Button>
              <TitleBar.Button>
                {/* Select File Input [TODO] - Web Only */}
                <Iffy is={featureFlags.enablePublisherUpload}>
                  <SelectFileButton
                    onSelect={onSelectedFiles}
                    size="md"
                    text="Upload"
                    multiple={true}
                    disableButtonGroup
                  />
                </Iffy>
              </TitleBar.Button>
            </TitleBar>
          </DNABox>
          <DNABox fill appearance="col">
            <Tabs
              selectedTab={selectedTab}
              selectedTabVariant="primary"
              tabs={tabs}
              onSelectTab={(tab) => {
                if (selectedTab !== tab) {
                  toggleUnpublished();
                }
              }}
              style={{ paddingTop: 12 }}
            />
            <DNADivider />
          </DNABox>
          {/* Content */}
          <DocumentDropzone
            files={filesToUpload}
            toggleMyUnpublishedDocument={() => {
              if (!unpublishedToggle) {
                toggleUnpublished()
              }
            }}
            disableDrag={!featureFlags.enablePublisherUpload}
            onCloseModal={() => { setFilesToUpload([]) }}
          >
            <DNABox appearance="col" spacing="md">
              <DNABox
                wrap="start"
                alignY="center"
                spacing="md"
                childStyle={[1, {
                  flexGrow: 1,
                  flexShrink: 1,
                  flexBasis: filtersMinimumWidth ?? 'auto',
                  maxWidth: '100%',
                }]}
                style={{ maxWidth :  fixedHeaderWidth, paddingTop: 16, paddingLeft: 12, marginBottom: -8, rowGap: 16 }}
              >
                <DNABox alignY="center" spacing="sm">
                  <FilterButton
                    setFiltersVisible={setFiltersVisible}
                    filtersVisible={filtersVisible}
                    variant={VariantOptions.publisher}
                  />
                  <DocumentTitleSearchInput
                    value={titleSearchText}
                    onChangeText={setTitleSearchText}
                  />
                </DNABox>
                <DNADocumentFiltersChips
                  documents={filteredDocuments}
                  onMinimumWidthChange={setFiltersMinimumWidth}
                />
              </DNABox>
              <DNADivider />
              <DNABox appearance="row">
                <ScrollView
                  style={util.mergeStyles(
                    undefined,
                    styles.filtersSidebarScrollView,
                    [styles.filersSidebarNotVisible, !filtersVisible],
                  )}
                >
                  <FiltersSidebar setVisible={setFiltersVisible} titleSearchText={titleSearchText} />
                </ScrollView>
                <DNABox style={{ paddingLeft: 16 }} fill>
                  <DNAPagination query={selectorOpts}>
                    <DNABox appearance="col" spacing="md" fill>
                      <DNABox style={filtersVisible ? { borderWidth: 1, borderColor: colors['color-gray-100'] } : {}}>
                        <DNAPublisherList />
                      </DNABox>
                      <DNABox style={{ alignSelf: 'flex-start', padding: 12 }}>
                        <DNAPagination.Nav />
                      </DNABox>
                    </DNABox>
                  </DNAPagination>
                </DNABox>
              </DNABox>
            </DNABox>
          </DocumentDropzone>
        </DNABox>
      </DNABox>

      <VersioningPanel
        documentId={documentId}
        setDocumentId={setDocumentId}
      />
    </QuickFiltersProvider>
  );
}

const DNAPublisherLibraryWithFilters = compose(
  withDNADocumentFilters,
  withSort,
)(DNAPublisherLibrary)

const DNAPublisherLibraryWithFeatureFlags: React.FC = () => {
  const applyDefaultFiltersToInProgress = useFeatureFlags('BEAC_8089_publisher_in_progress_default_filters')

  return (
    <DNAPublisherLibraryWithFilters
      applyDefaultFiltersToInProgress={applyDefaultFiltersToInProgress}
    />
  )
}

export default DNAPublisherLibraryWithFeatureFlags
